import { useCallback, useEffect, useRef, useState } from "react";
import { useAtom } from "jotai";
import { componentsAtom } from "../../../utils/atoms/componentsAtom";
import MGAAPI from "../../../api/index";
import SnackBarJs from "../../SnackBar.js";
import displayError from "../../../constants/utils.js";
import AddAsset from "../../Library/AddAsset.js";
import * as BATTERY_CONFIG from "./BATTERY_CONFIG.js";
import BatteryConfigWrapper from "./batteryConfigWrapper.js";
import useAssetManagement from "../../../hooks/useAssetManagement.js";
import { getErrorMessage } from "../../../utils/errorUtils.js";

const MGA_API = new MGAAPI();

const BatteryConfiguration = ({ handleInputChange, form }) => {
  const { setValues } = form;
  const snackBarRef = useRef(null);
  const [projectComponents, setProjectComponents] = useAtom(componentsAtom);
  const [snackBar, setSnackBar] = useState({ type: "", message: "" });
  const [batteryList, setBatteryList] = useState([]);
  const [batteryListData, setBatteryListData] = useState([]);
  const [addStatus, setAddStatus] = useState(false);
  const [popupStatus, setPopupStatus] = useState({
    addAssetPopupStatus: false,
    viewAssetPopupStatus: false,
    deleteConfirmPopUpStatus: false,
  });
  const [isBessSeletedDefault, setIsBessSelectedDefault] = useState(false);
  const [loading, setLoading] = useState(true);
  const [defaultAssetAttribute, setDefaultAssetAttribute] = useState([]);
  const [defaultAvailableAttributeName, setDefaultAvailableAttributeName] =
    useState([]);
  const [copyDefaultAttribute, setCopyDefaultAttribute] = useState([]);
  const [currentAvailableAttributes, setCurrentAvailableAttributes] = useState(
    []
  );
  const [isExtendedLibrary, setIsExtendedLibrary] = useState(false);
  const [assetInfo, setAssetInfo] = useState({
    allManufacturerList: [],
    manufacturerList: {},
  });
  const [renderFunction, setRenderFunction] = useState({
    renderAssetDetails: false,
  });

  const callSnackbar = useCallback((type, message) => {
    try {
      setSnackBar({ type, message });
      snackBarRef.current.open = true;
      if( type === "error" ){
        console.error(message);
        snackBarRef.current.canClose = true;
      }
    } catch (error) {
      console.log("error in snack bar");
    }
  }, []);

  // Save asset callback for the hook
  const saveAssetCallback = useCallback(
    async (data) => {
      try {
        const response = await MGA_API.saveAssets(data);
        assetManagement.resetAssetList();
        assetManagement.addUpdateDataToAssetList(response, addStatus);
        if (addStatus === true) {
          callSnackbar("success", "Asset added successfully");
        } else {
          callSnackbar("success", "Asset updated successfully");
        }
        BATTERY_CONFIG.handleAssetValues(
          assetManagement.setAssetDetails,
          setAddStatus,
          setLoading,
          setPopupStatus
        );
        try {
          const res = await MGA_API.getAssetCatalogue("BESS");
          BATTERY_CONFIG.handleFilteredAsset(res, setBatteryList);
        } catch (error) {
          displayError("error", "error while fetching Battery");
        }
      } catch (error) {
        BATTERY_CONFIG.handleAssetValues(
          assetManagement.setAssetDetails,
          setAddStatus,
          setLoading,
          setPopupStatus
        );
        callSnackbar("error", getErrorMessage(error));
      }
    },
    [addStatus, callSnackbar]
  );

  // Use the asset management hook
  const assetManagement = useAssetManagement({
    callSnackbar,
    defaultAssetAttribute,
    defaultAvailableAttributeName,
    copyDefaultAttribute,
    setCopyDefaultAttribute,
    setCurrentAvailableAttributes,
    currentAvailableAttributes,
    isExtendedLibrary,
    addStatus,
    setAddStatus,
    setLoading,
    setPopupStatus,
    saveAssetCallback,
  });

  const handleSelectBatteryChange = (value) => {
    BATTERY_CONFIG.handleProjectComponents(
      projectComponents,
      batteryListData,
      setProjectComponents,
      value,
      setValues
    );
  };

  const getDefaultAssetsAttributeList = () => {
      MGA_API.getDefaultsAssetsAttributes("BESS")
        .then((response) => {
          setDefaultAssetAttribute(response?.defaultAttributeList);
          setDefaultAvailableAttributeName(response?.allowedAttributeName);
        })
        .catch((error) => {
          console.log(error);
        });
  };

  const listAssetType = () => {
      MGA_API.getAssetType()
        .then((response) => {
          assetManagement.setAssetDetails((prevState) => ({
            ...prevState,
            assetsType: response.filter((a) => a.type === "BESS"),
          }));
          const renderFunctionsNew = { renderAssetDetails: true };
          setRenderFunction(renderFunctionsNew);
        })
        .catch(() => {});
  };

  const addAsset = () => {
    setAddStatus(true);
    setPopupStatus((prevState) => ({
      ...prevState,
      addAssetPopupStatus: true,
    }));
    setIsBessSelectedDefault(true);
  };

  useEffect(() => {
    MGA_API.getAssetCatalogue("BESS")
      .then((res) => {
        setBatteryListData(res?.assetCatalogue?.[0]?.assetList);
        BATTERY_CONFIG.handleFilteredAsset(res, setBatteryList);
      })
      .catch((res) => {
        console.log("dddd", res);
        displayError("error", "error while fetching Battery");
      });
  }, []);

  useEffect(() => {
    listAssetType();
    getDefaultAssetsAttributeList();
  }, []);

  useEffect(() => {
    BATTERY_CONFIG.handleAssetInfo(renderFunction, assetManagement.assetDetails, setAssetInfo);
  }, [renderFunction, assetManagement.assetDetails]);

  return (
    <div>
      <SnackBarJs snackBar={snackBar} ref={snackBarRef} />
      <BatteryConfigWrapper
        form={form}
        handleInputChange={handleInputChange}
        batteryParameters={projectComponents?.battery}
        batteryList={batteryList}
        handleSelectBatteryChange={handleSelectBatteryChange}
        addAsset={addAsset}
      />
      {popupStatus.addAssetPopupStatus && (
        <AddAsset
          getDefaultAssetsAttributeList={getDefaultAssetsAttributeList}
          addAssetPopupStatus={popupStatus.addAssetPopupStatus}
          addStatus={addStatus}
          isBessSeletedDefault={isBessSeletedDefault}
          assetsType={assetManagement.assetDetails?.assetsType}
          assetAttributes={assetManagement.assetDetails?.assetAttributes}
          assetDetailsData={assetManagement.assetDetails}
          manufacturerList={assetInfo.manufacturerList}
          updatedAsset={assetManagement.updatedAsset}
          onCloseAssetPopUp={assetManagement.onCloseAssetPopUp}
          onUpdateAsset={assetManagement.onUpdateAsset}
          getAssetDetailsAttribute={assetManagement.getAssetDetailsAttribute}
          addAttributeToLibrary={assetManagement.addAttributeToLibrary}
          handleAsset={assetManagement.handleAsset}
          handleAssetSelection={assetManagement.handleAssetSelection}
          asset={assetManagement.asset}
          projectTypes={[]}
          handleEditAsset={assetManagement.handleEditAsset}
          handleDeleteAsset={assetManagement.handleDeleteAsset}
          handleAssetAttribute={assetManagement.handleAssetAttribute}
          handleOkEditAsset={assetManagement.handleOkEditAsset}
          handleCancelUpdateAsset={assetManagement.handleCancelUpdateAsset}
          setIsExtendedLibrary={setIsExtendedLibrary}
          isExtendedLibrary={isExtendedLibrary}
          inputRefs={assetManagement.inputRefs}
          currentAvailableAttributes={currentAvailableAttributes}
          defaultAssetAttribute={defaultAssetAttribute}
          setAssetDetailsEx={assetManagement.setAssetDetails}
          setCurrentAvailableAttributes={setCurrentAvailableAttributes}
          isAssetTypeDisabled={true}
        />
      )}
    </div>
  );
};
export default BatteryConfiguration;
